import React from "react";
import { Chip } from "@mui/material";
import { colors, fonts } from "../../../styles/theme";
import { StatusVisual } from "../../../utils/colors/statusVisual";

/**
 * Darstellung des Chips:
 * - "filled" (Standard): getönter Hintergrund, Schrift in der Farbe, halbfett
 * - "solid": voll in der Farbe, weiße Schrift, leichter Schatten – der hervorgehobene Zustand
 *   einer Reihe, etwa der aktuelle Schritt
 * - "outlined": nur Rand und Schrift in der Farbe, ohne Hintergrund
 * - "subtle": getönter Hintergrund wie "filled", aber normale Schrift
 * "outlined" und "subtle" treten neben einem "solid" zurück.
 */
type StatusChipVariante = "filled" | "solid" | "outlined" | "subtle";

interface StatusChipProps extends StatusVisual {
  size?: "small" | "medium";
  variante?: StatusChipVariante;
}

/**
 * Status Chip Component
 *
 * Bewusst domänenfrei: Die Komponente bekommt Label und Farben fertig übergeben und kennt
 * keine Fachdomäne. Aufgelöst wird der Rohstatus in der jeweiligen Domänen-Util, z.B.
 * `getEvidenceStatusVisual` oder `getParcelStatusVisual`:
 *
 *   <StatusChip {...getEvidenceStatusVisual(evidence.status)} />
 */
const StatusChip: React.FC<StatusChipProps> = ({
  label,
  color,
  background,
  size = "small",
  variante = "filled",
}) => {
  const hintergrund =
    variante === "solid"
      ? color
      : variante === "outlined"
        ? "transparent"
        : background;
  return (
    <Chip
      label={label}
      size={size}
      sx={{
        backgroundColor: hintergrund,
        color: variante === "solid" ? colors.text.onDark : color,
        fontWeight:
          variante === "outlined" || variante === "subtle"
            ? fonts.weights.regular
            : fonts.weights.semiBold,
        border: variante === "outlined" ? `1px solid ${color}` : "none",
        boxShadow:
          variante === "solid" ? `0 2px 6px ${colors.shadow.heavy}` : "none",
        "&.MuiChip-filled": { backgroundColor: hintergrund },
      }}
    />
  );
};

export default StatusChip;
